import React, { useState } from 'react';
import { useTheme } from 'tamagui';
import dayjs from 'dayjs';
import { Strings } from '../../constants/Strings';
import { CobuStack, CobuText, CobuButton, CobuContainer } from '@cobu/ui';
import CheckInModal from '../ui/CheckInModal';
import { Gathering, RSVP, UserInfo } from '@cobu/types';
import { getEventFee, formatEventFee } from '../../lib/events/rateDetails';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface EventDetailFooterProps {
  gathering: Gathering;
  rsvpObj: RSVP | null;
  currentUser: UserInfo | null;
  totalAttendance: number;
  rsvpTotalCapacity?: number | null;
  onRSVPPress: (totalAttendees?: number) => Promise<void>;
  onCancelRSVP: () => Promise<void>;
  onUpdateRSVP: (totalAttendees: number) => Promise<void>;
  isRsvpActionPending?: boolean;
  isParentDetailsLoading?: boolean;
  onClose: () => void;
}

export function EventDetailFooter({
  gathering,
  rsvpObj,
  currentUser,
  totalAttendance,
  rsvpTotalCapacity,
  onRSVPPress,
  onCancelRSVP,
  onUpdateRSVP,
  isRsvpActionPending = false,
  isParentDetailsLoading = false,
  onClose,
}: EventDetailFooterProps) {
  const theme = useTheme();
  const strings = Strings.get();
  const [showGuestModal, setShowGuestModal] = useState(false);
  const hasExistingRSVP = !!rsvpObj;
  const handleModalClose = () => setShowGuestModal(false);

  // Check if event is cancelled
  if (gathering.cancelled) {
    return (
      <CobuContainer
        pos="fixed"
        b={0}
        l={0}
        r={0}
        zi={1000}
        bg="$background"
        p="$4"
        bbc="$borderColor"
        bbw={1}
        elevation={4}
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
      >
        <CobuStack ai="center" gap="$3">
          <CobuButton
            title={strings.events.detail.buttons.findGatherings}
            dsVariant="primary"
            onPress={onClose}
            f={1}
          />
        </CobuStack>
      </CobuContainer>
    );
  }

  const isRecurring = !!gathering.recurringType;
  const isRecurringOrPartner = isRecurring || !!gathering.isPartnerEvent;
  const effectiveEndTime = gathering.scheduledEndTime
    ? dayjs(gathering.scheduledEndTime)
    : isRecurringOrPartner
      ? dayjs(gathering.scheduledStartTime).add(1, 'hour')
      : null;
  if (effectiveEndTime?.isBefore(dayjs())) {
    return null;
  }

  // Calculate button text and RSVP status based on commitType
  const getButtonConfig = () => {
    let buttonText = strings.events.detail.buttons.rsvp;
    let isRSVP = false;

    switch (gathering.commitType) {
      case '45890325-4e1d-4364-8aea-a13b2cf4c02c':
        buttonText = strings.events.detail.buttons.expressInterest;
        isRSVP = false;
        break;
      case 'b4a735d1-581b-4321-85a9-818ce6b2ff0a':
        buttonText = strings.events.detail.buttons.register;
        isRSVP = true;
        break;
      default:
        buttonText = strings.events.detail.buttons.rsvp;
        isRSVP = true;
        break;
    }

    /*
    e8a642e9-7fe0-4f5a-9279-4cec3db10dd2	RSVP
    45890325-4e1d-4364-8aea-a13b2cf4c02c	Interested
    b4a735d1-581b-4321-85a9-818ce6b2ff0a	Register    
    */

    // Paid events swap the CTA for an explicit price, so residents know a
    // charge is coming before they commit.
    const fee = getEventFee(gathering.rateDetails);
    if (isRSVP && fee != null) {
      buttonText = strings.events.detail.buttons.payToRsvp.replace(
        '{amount}',
        formatEventFee(fee)
      );
    }

    return { buttonText, isRSVP };
  };

  const { buttonText, isRSVP } = getButtonConfig();

  const isAtParentCapacity =
    rsvpTotalCapacity !== undefined &&
    rsvpTotalCapacity !== null &&
    totalAttendance >= rsvpTotalCapacity;
  const isLoading = isRsvpActionPending || isParentDetailsLoading;
  const isDisabled =
    (isRSVP && !hasExistingRSVP && isAtParentCapacity) ||
    (rsvpTotalCapacity === undefined && isRSVP && gathering.maxEventAttendees != null && gathering.maxEventAttendees <= 0);

  // Handle RSVP/Change button press
  const handleRSVPPress = () => {
    setShowGuestModal(true);
  };

  if (!hasExistingRSVP) {
    // Show RSVP button
    return (
      <CobuContainer
        pos="fixed"
        b={0}
        l={0}
        r={0}
        zi={1000}
        bg="$background"
        p="$4"
        bbc="$borderColor"
        bbw={1}
        elevation={4}
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
      >
        <CobuButton
          variant="primary"
          title={isAtParentCapacity ? strings.events.detail.labels.eventFull : isDisabled ? strings.events.detail.labels.rsvpWillOpenSoon : buttonText}
          loading={isLoading}
          loadingText={isRsvpActionPending ? strings.events.detail.buttons.updating : strings.common.loading}
          onPress={() => {
            if (isDisabled || isLoading) return;
            if (gathering.externalEvent) {
              onRSVPPress(1);
            } else {
              setShowGuestModal(true);
            }
          }}
          disabled={isDisabled}
        />
        {!gathering.externalEvent && showGuestModal ? (
          <CheckInModal
            visible
            onClose={handleModalClose}
            gathering={gathering}
            maxGuests={gathering.maxGuests}
            currentTotalAttendees={1}
            hasExistingRSVP={false}
            onConfirm={async (count) => {
              await onRSVPPress(count);
            }}
            isSubmitting={isRsvpActionPending}
          />
        ) : null}
      </CobuContainer>
    );
  }

  // Calculate attending state text - for attending users, recalculate isRSVP
  const attendingIsRSVP = gathering.commitType !== '45890325-4e1d-4364-8aea-a13b2cf4c02c';
  const attendingText = attendingIsRSVP
    ? strings.events.detail.buttons.attending
    : strings.events.detail.buttons.interested;

  // Show attending state with change option (matches RN footer layout)
  return (
    <CobuContainer
      pos="fixed"
      b={0}
      l={0}
      r={0}
      zi={1000}
      bg="$background"
      bbc="$borderColor"
      bbw={1}
      elevation={4}
      p="$3"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
    >
      <CobuStack layout="horizontal" ai="center" gap="$3">
        {gathering.externalEvent ? (
          <CobuButton
            title={(strings.events.detail.alerts.cannotAttend || 'I Cannot Attend').toUpperCase()}
            variant="ghost"
            onPress={onCancelRSVP}
            loading={isRsvpActionPending}
            loadingText={strings.events.detail.buttons.updating}
            f={1}
          />
        ) : (
          <CobuButton
            title={(strings.events.detail.buttons.change || 'Change').toUpperCase()}
            variant="ghost"
            onPress={handleRSVPPress}
            disabled={isRsvpActionPending}
            f={1}
          />
        )}
        {/* Middle: Status text centered */}
        <CobuStack f={1} ai="center" jc="center">
          <CobuText variant="label" color="$cobuDarkBlue" fontWeight="bold" fontSize="$3">
            {(attendingText || '').toUpperCase()}
          </CobuText>
        </CobuStack>
        <CobuStack
          w={36 as any}
          h={36 as any}
          br="$full"
          ai="center"
          jc="center"
          bg="$cobuTopaz"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden style={{ display: 'block', color: '#ffffff' }}>
            <path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </CobuStack>
      </CobuStack>
      {!gathering.externalEvent && showGuestModal ? (
        <CheckInModal
          visible
          onClose={handleModalClose}
          gathering={gathering}
          maxGuests={gathering.maxGuests}
          currentTotalAttendees={rsvpObj?.totalAttendees ?? 1}
          hasExistingRSVP
          onConfirm={async (count) => {
            await onUpdateRSVP(count);
          }}
          onCancelRSVP={async () => {
            await onCancelRSVP();
          }}
          isSubmitting={isRsvpActionPending}
        />
      ) : null}
    </CobuContainer>
  );
}
